/**
 * Centralized logging utility with TI-M prefix
 *
 * Usage:
 *   // In any component or utility file
 *   import logger from '../utils/logger';
 *   logger.error('Something went wrong:', error);
 *   logger.warn('Warning message');
 *   logger.info('Info message');
 *   logger.debug('Debug info');
 *
 * All logs are prefixed with "TI-M" for easy filtering.
 * The logger is environment-aware - console statements only appear in development.
 * In production, you can extend this to send logs to error tracking services.
 */

const isDevelopment = process.env.NODE_ENV === 'development';
const LOG_PREFIX = 'TI-M';

// Log level configuration - can be overridden via localStorage
const getLogLevel = () => {
  try {
    const storedLevel = localStorage.getItem('TI-M_LOG_LEVEL');
    return storedLevel || (isDevelopment ? 'debug' : 'error');
  } catch {
    return isDevelopment ? 'debug' : 'error';
  }
};

const LOG_LEVELS = {
  error: 0,
  warn: 1,
  info: 2,
  debug: 3,
};

const shouldLog = (level) => {
  const currentLevel = getLogLevel();
  return LOG_LEVELS[level] <= LOG_LEVELS[currentLevel];
};

// Format message with timestamp and prefix
const formatMessage = (level, message) => {
  const timestamp = new Date().toISOString();
  const levelTag = level.toUpperCase().padEnd(5);
  return `[${LOG_PREFIX}] [${timestamp}] [${levelTag}] ${message}`;
};

// Utility to redact sensitive data in logs
const redactSensitive = (data, visited = new WeakSet()) => {
  // Expand Error objects to plain info so message/stack are visible
  if (data instanceof Error) {
    return {
      name: data.name,
      message: data.message,
      stack: data.stack,
      code: data.code,
    };
  }
  if (typeof data === 'string') {
    // Redact recovery keys (48 chars base58 in groups)
    return data.replace(/([A-HJ-NP-Za-km-z]{4}\s){11}[A-HJ-NP-Za-km-z]{4}/g, '[REDACTED_RECOVERY_KEY]')
               // Redact access tokens
               .replace(/Bearer\s+[A-Za-z0-9_-]+/g, 'Bearer [REDACTED_TOKEN]')
               // Redact passwords
               .replace(/"password":\s*"[^"]+"/g, '"password": "[REDACTED]"');
  }
  if (typeof data === 'object' && data !== null) {
    // Check for circular references
    if (visited.has(data)) {
      return '[Circular Reference]';
    }

    // Add current object to visited set
    visited.add(data);

    // Handle arrays
    if (Array.isArray(data)) {
      return data.map(item => redactSensitive(item, visited));
    }

    // Handle regular objects
    const redacted = {};
    try {
      for (const [key, value] of Object.entries(data)) {
        // More specific redaction rules - avoid false positives like "key" property
        const keyLower = key.toLowerCase();
        if (keyLower === 'password' ||
            keyLower.includes('password') ||
            keyLower === 'token' ||
            keyLower.includes('accesstoken') ||
            keyLower.includes('refreshtoken') ||
            keyLower === 'recoverykey' ||
            keyLower === 'privatekey' ||
            keyLower === 'secretkey' ||
            keyLower === 'apikey') {
          redacted[key] = '[REDACTED]';
        } else {
          redacted[key] = redactSensitive(value, visited);
        }
      }
    } catch {
      // If we can't iterate the object, return a placeholder
      return {
        type: Object.prototype.toString.call(data),
      };
    }
    return redacted;
  }
  return data;
};

const logger = {
  error: (message, ...args) => {
    if (shouldLog('error')) {
      const formattedMessage = formatMessage('error', message);
      // Create new WeakSet for each logging call to handle independent object graphs
      const redactedArgs = args.map(arg => redactSensitive(arg, new WeakSet()));
      // eslint-disable-next-line no-console
      console.error(formattedMessage, ...redactedArgs);
    }
    // In production, you could send to an error tracking service
    // like Sentry, LogRocket, or your own logging endpoint
    // Example: errorTrackingService.captureException(new Error(message));
  },

  warn: (message, ...args) => {
    if (shouldLog('warn')) {
      const formattedMessage = formatMessage('warn', message);
      // Create new WeakSet for each logging call to handle independent object graphs
      const redactedArgs = args.map(arg => redactSensitive(arg, new WeakSet()));
      // eslint-disable-next-line no-console
      console.warn(formattedMessage, ...redactedArgs);
    }
    // Similar production logging could go here
  },

  info: (message, ...args) => {
    if (shouldLog('info')) {
      const formattedMessage = formatMessage('info', message);
      // Create new WeakSet for each logging call to handle independent object graphs
      const redactedArgs = args.map(arg => redactSensitive(arg, new WeakSet()));
      // eslint-disable-next-line no-console
      console.log(formattedMessage, ...redactedArgs);
    }
  },

  debug: (message, ...args) => {
    if (shouldLog('debug')) {
      const formattedMessage = formatMessage('debug', message);
      // Create new WeakSet for each logging call to handle independent object graphs
      const redactedArgs = args.map(arg => redactSensitive(arg, new WeakSet()));
      // eslint-disable-next-line no-console
      console.debug(formattedMessage, ...redactedArgs);
    }
  },

  // Performance logging
  time: (label) => {
    if (shouldLog('debug')) {
      const formattedLabel = `${LOG_PREFIX}:${label}`;
      // eslint-disable-next-line no-console
      console.time(formattedLabel);
    }
  },

  timeEnd: (label) => {
    if (shouldLog('debug')) {
      const formattedLabel = `${LOG_PREFIX}:${label}`;
      // eslint-disable-next-line no-console
      console.timeEnd(formattedLabel);
    }
  },

  // Group logs for better organization
  group: (label) => {
    if (shouldLog('debug')) {
      const formattedLabel = formatMessage('group', label);
      // eslint-disable-next-line no-console
      console.group(formattedLabel);
    }
  },

  groupEnd: () => {
    if (shouldLog('debug')) {
      // eslint-disable-next-line no-console
      console.groupEnd();
    }
  },

  // Set log level dynamically
  setLogLevel: (level) => {
    if (LOG_LEVELS[level] !== undefined) {
      localStorage.setItem('TI-M_LOG_LEVEL', level);
      logger.info(`Log level set to: ${level}`);
    } else {
      logger.error(`Invalid log level: ${level}. Valid levels are: ${Object.keys(LOG_LEVELS).join(', ')}`);
    }
  },
};

export default logger;
